import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { formatTimestamp } from '../utils/timestampUtils';
import './TimestampEditor.css';

const TimestampShiftDialog = ({
  selectedWords,
  onApply,
  onCancel
}) => {
  const [offsetValue, setOffsetValue] = useState('0.000');
  const [sliderValue, setSliderValue] = useState(0);
  const dialogRef = useRef(null);
  const inputRef = useRef(null);

  // Calculate range info
  const firstWord = selectedWords[0];
  const lastWord = selectedWords[selectedWords.length - 1];
  const rangeStart = firstWord?.start || 0;
  const rangeEnd = lastWord?.end || 0;

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Enter') {
        handleApply();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, offsetValue]);

  const handleSliderChange = useCallback((e) => {
    const value = parseFloat(e.target.value);
    setSliderValue(value);
    setOffsetValue(value >= 0 ? `+${value.toFixed(3)}` : value.toFixed(3));
  }, []);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setOffsetValue(value);

    // Try to parse and update slider
    const parsed = parseFloat(value.replace('+', ''));
    if (!isNaN(parsed) && parsed >= -10 && parsed <= 10) {
      setSliderValue(parsed);
    }
  }, []);

  const handleApply = useCallback(() => {
    const parsed = parseFloat(offsetValue.replace('+', ''));
    if (isNaN(parsed)) return;

    onApply(parsed);
  }, [offsetValue, onApply]);

  const offset = parseFloat(offsetValue.replace('+', '')) || 0;
  const newRangeStart = Math.max(0, rangeStart + offset);
  const newRangeEnd = rangeEnd + offset;

  return createPortal(
    <>
      <div className="timestamp-dialog-backdrop" onClick={onCancel} />
      <div ref={dialogRef} className="timestamp-shift-dialog">
        <div className="dialog-header">
          <span className="dialog-title">Shift Timestamps</span>
          <button className="dialog-close" onClick={onCancel}>×</button>
        </div>

        <div className="dialog-body">
          <div className="dialog-info">
            Selected: {selectedWords.length} words
            ({formatTimestamp(rangeStart)} - {formatTimestamp(rangeEnd)})
          </div>

          <div className="dialog-input-group">
            <label>Offset (seconds)</label>
            <div className="dialog-input-row">
              <input
                ref={inputRef}
                type="text"
                value={offsetValue}
                onChange={handleInputChange}
                placeholder="0.000"
              />
              <span style={{ color: '#888', fontSize: '13px' }}>seconds</span>
            </div>
            <input
              type="range"
              className="dialog-slider"
              min="-5"
              max="5"
              step="0.01"
              value={sliderValue}
              onChange={handleSliderChange}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666' }}>
              <span>-5s</span>
              <span>0</span>
              <span>+5s</span>
            </div>
          </div>

          <div className="dialog-preview">
            New range: {formatTimestamp(newRangeStart)} - {formatTimestamp(newRangeEnd)}
          </div>
        </div>

        <div className="dialog-footer">
          <button className="cancel-btn" onClick={onCancel}>Cancel</button>
          <button className="save-btn" onClick={handleApply}>Apply</button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TimestampShiftDialog;
